import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Store, Hash, ArrowLeft, Loader2, Settings, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import POSManifestSwapper from '@/components/POSManifestSwapper';
import InstallPOSPrompt from '@/components/InstallPOSPrompt';
import { KioskToggleControl } from '@/components/KioskLockScreen';
import { PosLoginBrand } from '@/components/pos/PosLoginBrand';
import { getDeviceId } from '@/lib/deviceHeartbeat';

export default function CashierLoginPage() {
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  // iter — Terminal-branch setup gate. A device must have a branch assigned
  // (pos_terminal_branch_id) before PIN login, otherwise the backend returns
  // "Branch required". setupReason: '' (configured) | 'missing' | 'invalid'.
  const [setupReason, setSetupReason] = useState('');
  useEffect(() => {
    let b = '';
    try { b = localStorage.getItem('pos_terminal_branch_id') || ''; } catch { /* ignore */ }
    setSetupReason(b ? '' : 'missing');
  }, []);
  const goAdminSetup = () => navigate('/settings/printers?terminalSetup=1&returnTo=/cashier');

  const handlePinChange = (value) => {
    // Only allow numbers and max 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setPin(numericValue);
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (pin.length < 4) {
      toast.error('Please enter a 4-digit PIN');
      return;
    }
    
    setLoading(true);
    try {
      // iter689 — Send the terminal's configured branch_id so the
      // server can stamp the cashier's session under THAT branch
      // (not just the cashier's "home" branch). This is what lets a
      // cashier from Branch A work a shift at Branch B without sales
      // being mis-attributed.
      let terminalBranchId = '';
      try {
        terminalBranchId = localStorage.getItem('pos_terminal_branch_id') || '';
      } catch { /* ignore */ }
      const { data } = await api.post('/cashier/login', {
        pin,
        device_id: getDeviceId(),
        ...(terminalBranchId ? { branch_id: terminalBranchId } : {}),
      });
      const posRole = data.user?.pos_role || 'both';
      
      // Check if user has cashier access
      if (posRole === 'waiter') {
        toast.error('This PIN is for waiter access only. Please use Waiter Mode.');
        setPin('');
        setLoading(false);
        return;
      }
      
      localStorage.setItem('cashier_token', data.access_token);
      if (data.device_assigned_branch_id) localStorage.setItem('pos_terminal_branch_id', data.device_assigned_branch_id);
      localStorage.setItem('cashier_user', JSON.stringify(data.user));
      toast.success(`Welcome, ${data.user.name}!`);
      navigate('/cashier/pos');
    } catch (err) {
      if (err.response?.status === 404) {
        // Stale/invalid stored terminal branch — do NOT silently fall back.
        setSetupReason('invalid');
      } else {
        toast.error(err.response?.data?.detail || 'Invalid PIN');
      }
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  // Handle keypad input
  const handleKeypad = (num) => {
    if (num === 'clear') {
      setPin('');
    } else if (num === 'back') {
      setPin(pin.slice(0, -1));
    } else if (pin.length < 6) {
      const newPin = pin + num;
      setPin(newPin);
      // Auto-submit on 4 digits
      if (newPin.length === 4) {
        setTimeout(() => {
          handleLogin();
        }, 300);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-500 via-orange-400 to-amber-400 flex items-center justify-center p-3 pb-24 overflow-y-auto">
      {/* iter601 — swap manifest to POS-only so iPad "Add to Home Screen"
          launches the cashier PIN page in standalone mode, not the
          marketing site at "/". Auto-restores on unmount. */}
      <POSManifestSwapper />
      <InstallPOSPrompt />
      {setupReason ? (
        <Card className="w-full max-w-sm my-auto shadow-2xl border-0 overflow-hidden" data-testid="terminal-setup-card">
          <div className="bg-white p-6 text-center border-b">
            <PosLoginBrand role="Cashier POS" icon={Store} showVersion />
          </div>
          <CardContent className="p-6 bg-muted/50 text-center space-y-4">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="text-amber-600" size={28} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">This Terminal Is Not Set Up</h2>
              <p className="text-sm text-muted-foreground mt-1" data-testid="terminal-setup-message">
                {setupReason === 'invalid'
                  ? 'Terminal branch configuration is no longer valid — Admin Setup required.'
                  : 'A branch must be assigned to this device before Cashier can be used.'}
              </p>
            </div>
            <Button onClick={goAdminSetup} className="w-full h-12 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold gap-2" data-testid="terminal-admin-setup-btn">
              <Settings size={18} /> Admin Setup
            </Button>
            <a href="/login" className="text-xs text-orange-600 hover:underline inline-flex items-center gap-1 justify-center">
              <ArrowLeft size={12} /> Main Login
            </a>
            <div className="pt-1"><KioskToggleControl /></div>
          </CardContent>
        </Card>
      ) : (
      <Card className="w-full max-w-sm my-auto shadow-2xl border-0 overflow-hidden" data-testid="cashier-login-card">
        {/* Header — shared pre-auth brand (company logo + app name + role + native version) */}
        <div className="bg-white p-6 landscape:p-4 text-center border-b">
          <PosLoginBrand role="Cashier POS" icon={Store} showVersion />
        </div>

        <CardContent className="p-6 landscape:p-4 bg-muted/50">
          {/* PIN Display */}
          <div className="mb-6 landscape:mb-3">
            <div className="flex justify-center gap-3 mb-2">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`w-12 h-14 landscape:h-11 rounded-xl border-2 flex items-center justify-center text-2xl font-bold font-mono transition-all ${
                    pin[i] 
                      ? 'bg-orange-500 border-orange-500 text-white' 
                      : 'bg-white border-border'
                  }`}
                >
                  {pin[i] ? '•' : ''}
                </div>
              ))}
            </div>
            {loading && (
              <div className="flex items-center justify-center gap-2 text-orange-600">
                <Loader2 size={16} className="animate-spin" />
                <span className="text-sm">Verifying...</span>
              </div>
            )}
          </div>

          {/* Number Keypad — MUST stay 3 columns on every viewport size.
              A phone-style 3×4 keypad is not a data grid; the earlier
              responsive-columns bulk transform (iter822) accidentally
              broke this. iter827 pins it back to grid-cols-3. */}
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <Button
                key={num}
                type="button"
                variant="outline"
                className="h-14 landscape:h-12 text-xl font-bold bg-white hover:bg-orange-50 hover:border-orange-300 rounded-xl"
                onClick={() => handleKeypad(num.toString())}
                disabled={loading}
                data-testid={`keypad-${num}`}
              >
                {num}
              </Button>
            ))}
            <Button
              type="button"
              variant="outline"
              className="h-14 landscape:h-12 text-sm font-medium bg-error/10 hover:bg-error/20 border-error/30 text-error rounded-xl"
              onClick={() => handleKeypad('clear')}
              disabled={loading}
              data-testid="keypad-clear"
            >
              Clear
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-14 landscape:h-12 text-xl font-bold bg-white hover:bg-orange-50 hover:border-orange-300 rounded-xl"
              onClick={() => handleKeypad('0')}
              disabled={loading}
              data-testid="keypad-0"
            >
              0
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-14 landscape:h-12 bg-muted hover:bg-muted rounded-xl"
              onClick={() => handleKeypad('back')}
              disabled={loading}
              data-testid="keypad-back"
            >
              <ArrowLeft size={20} />
            </Button>
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleLogin}
            className="w-full h-12 landscape:h-11 mt-4 landscape:mt-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-base"
            disabled={loading || pin.length < 4}
            data-testid="cashier-login-btn"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </Button>

          {/* Links */}
          <div className="mt-4 flex justify-between text-xs">
            <a href="/login" className="text-orange-600 hover:underline flex items-center gap-1">
              <ArrowLeft size={12} />
              Main Login
            </a>
            <a href="/waiter" className="text-muted-foreground hover:underline">
              Waiter Mode
            </a>
            <a href="/kds" className="text-muted-foreground hover:underline">
              Kitchen Display →
            </a>
          </div>

          <KioskToggleControl />
        </CardContent>
      </Card>
      )}
    </div>
  );
}
